import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { ApiError } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { useBackTo } from "@/hooks/useBackTo";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loading } from "@/components/Loading";
import { Notice } from "@/components/Notice";
import { StatusPill, USER_TIER_TONES } from "@/components/StatusPill";
import { useConfirm } from "@/components/ConfirmDialog";
import { CommentAvatar } from "@/features/conversations/components/MessageCard";
import { USER_ROLE_TIER_LABELS } from "@/features/auth/api/auth.api";
import { UserSelect } from "@/features/users/components/UserSelect";
import {
  users,
  type UserDeletionGroup,
  type UserDeletionItem,
  type UserDeletionSummary,
} from "@/features/users/api/users.api";

const NONE = "__none__";
const SELECT_USER_OPTIONS = [{ value: NONE, label: "Select a user…" }];
const TARGET_REQUIRED = "Select a user to take over this data before deleting.";
const DESCRIPTION =
  'Permanently removes this user. Requires the "Allow to Delete User" permission (70.05.15). ' +
  "Review what they own before deleting. This can't be undone.";

const GROUP_ORDER: UserDeletionGroup[] = ["merchants", "tickets", "sales", "applications", "partner", "team", "history"];

const GROUP_LABELS: Record<UserDeletionGroup, string> = {
  team: "Team",
  merchants: "Merchants",
  applications: "Applications",
  tickets: "Tickets",
  sales: "Sales",
  partner: "Partner forms",
  history: "History",
  credentials: "Access",
};

const ACTIVE_LABELS: Record<string, string> = {
  merchantsOwned: "live",
  ticketsAssigned: "open",
  applicationsOwned: "in progress",
  quotes: "open",
  recurringInvoices: "active",
};

function plural(n: number, one: string, many = `${one}s`) {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

function displayName(s: Pick<UserDeletionSummary, "name" | "email">) {
  return s.name || s.email || "This user";
}

function otherRecordCount(s: UserDeletionSummary) {
  return Math.max(0, s.totalToTransfer - s.merchants.total - s.tickets.assignedTotal);
}

function footerSummary(s: UserDeletionSummary) {
  if (!s.canDelete) return "Deleting is blocked.";
  if (!s.requiresReassignment) return "Nothing to transfer.";
  return `${plural(s.totalToTransfer, "record")} will change owner.`;
}

function summaryErrorMessage(error: unknown) {
  if (error instanceof ApiError && error.status === 403) {
    return "You don't have permission to delete users, so this user's data can't be reviewed here.";
  }
  if (error instanceof ApiError && error.status === 404) {
    const code = (error.body as { errorCode?: unknown } | null)?.errorCode;
    return code === "NOT_FOUND"
      ? "This user no longer exists."
      : "This server doesn't support reviewing a user before deletion yet.";
  }
  return error instanceof Error ? error.message : "Couldn't load what this user owns.";
}

export interface DeletionSummaryState {
  summary: UserDeletionSummary | null;
  loading: boolean;
  error: unknown;
  reload: () => void;
}

export function useDeletionSummary(userId: string): DeletionSummaryState {
  const [summary, setSummary] = useState<UserDeletionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    users
      .deletionSummary(userId)
      .then((s) => { if (alive) setSummary(s); })
      .catch((e) => {
        if (!alive) return;
        setSummary(null);
        setError(e);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { summary, loading, error, reload };
}

interface DeleteUserFlow {
  reassignTo: string;
  setReassignTo: (id: string) => void;
  targetError: string | null;
  deleting: boolean;
  deleteError: string | null;
  stale: boolean;
  submit: () => Promise<void>;
}

function useDeleteUser(
  userId: string,
  deletion: DeletionSummaryState,
  onDeleted: (message: string) => void,
): DeleteUserFlow {
  const confirm = useConfirm();
  const { summary, reload } = deletion;
  const [reassignTo, setReassignToState] = useState("");
  const [targetError, setTargetError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  const suggested = summary?.suggestedReassignTo ?? null;
  useEffect(() => {
    if (suggested && suggested.id !== userId) setReassignToState((cur) => cur || suggested.id);
  }, [suggested, userId]);

  function setReassignTo(id: string) {
    setReassignToState(id);
    if (id) setTargetError(null);
  }

  async function submit() {
    if (!summary || !summary.canDelete || deleting) return;
    setDeleteError(null);
    const target = summary.requiresReassignment ? reassignTo : undefined;
    if (summary.requiresReassignment && !target) {
      setTargetError(TARGET_REQUIRED);
      return;
    }

    const name = displayName(summary);
    let targetName: string | null = null;
    if (target) {
      targetName = target === suggested?.id
        ? suggested.name
        : (await users.get(target).catch(() => null))?.name || "the chosen user";
    }

    const ok = await confirm({
      title: "Delete user?",
      description: (
        <>
          Delete <strong className="font-medium text-foreground">{summary.email || name}</strong>? This can't be
          undone.
        </>
      ),
      details: <ConsequenceList summary={summary} targetName={targetName} />,
      confirmLabel: "Delete user",
      destructive: true,
    });
    if (!ok) return;

    setDeleting(true);
    try {
      const res = await users.remove(userId, target);
      const moved = res?.rowsReassigned ?? summary.totalToTransfer;
      onDeleted(
        targetName && moved > 0
          ? `${name} deleted. ${plural(moved, "record")} transferred to ${targetName}.`
          : `${name} deleted.`,
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setStale(true);
        reload();
      } else {
        setDeleteError(e instanceof Error ? e.message : "Delete failed.");
      }
    } finally {
      setDeleting(false);
    }
  }

  return { reassignTo, setReassignTo, targetError, deleting, deleteError, stale, submit };
}

export function DeleteUserCard({ userId, deletion }: { userId: string; deletion: DeletionSummaryState }) {
  const navigate = useNavigate();
  const backTo = useBackTo("/manage/users");
  const flow = useDeleteUser(userId, deletion, (message) => {
    toast.success(message);
    navigate(backTo, { replace: true });
  });
  const { summary, loading } = deletion;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <h3 className="text-base font-semibold text-slate-900 mb-1">Delete user</h3>
      <p className="text-sm text-slate-500 mb-5">{DESCRIPTION}</p>

      <SummaryStatus deletion={deletion} />

      {summary && (
        <>
          <DeleteUserReview userId={userId} summary={summary} flow={flow} narrow />
          <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-4">
            <Button
              type="button"
              variant="destructive"
              onClick={flow.submit}
              disabled={!summary.canDelete || flow.deleting || loading}
            >
              {flow.deleting ? "Deleting…" : "Delete user"}
            </Button>
            <span className="text-sm text-slate-500">{footerSummary(summary)}</span>
          </div>
          {flow.deleteError && <p className="mt-2 text-sm text-destructive">{flow.deleteError}</p>}
        </>
      )}
    </div>
  );
}

export function DeleteUserDialog({
  userId,
  onClose,
  onDeleted,
}: {
  userId: string | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  return (
    <Dialog open={userId != null} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        {userId != null && <DeleteUserDialogBody userId={userId} onClose={onClose} onDeleted={onDeleted} />}
      </DialogContent>
    </Dialog>
  );
}

function DeleteUserDialogBody({
  userId,
  onClose,
  onDeleted,
}: {
  userId: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const deletion = useDeletionSummary(userId);
  const flow = useDeleteUser(userId, deletion, (message) => {
    toast.success(message);
    onDeleted();
  });
  const { summary, loading } = deletion;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Delete user</DialogTitle>
        <DialogDescription>{DESCRIPTION}</DialogDescription>
      </DialogHeader>

      <div>
        <SummaryStatus deletion={deletion} />
        {summary && <DeleteUserReview userId={userId} summary={summary} flow={flow} showIdentity />}
        {flow.deleteError && <p className="mt-3 text-sm text-destructive">{flow.deleteError}</p>}
      </div>

      <DialogFooter className="gap-2 sm:items-center">
        {summary && <span className="text-sm text-slate-500 sm:mr-auto">{footerSummary(summary)}</span>}
        <Button type="button" variant="ghost" onClick={onClose} disabled={flow.deleting}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={flow.submit}
          disabled={!summary?.canDelete || flow.deleting || loading}
        >
          {flow.deleting ? "Deleting…" : "Delete user"}
        </Button>
      </DialogFooter>
    </>
  );
}

function SummaryStatus({ deletion }: { deletion: DeletionSummaryState }) {
  const { summary, loading, error, reload } = deletion;
  if (loading && !summary) return <Loading>Checking what this user owns…</Loading>;
  if (loading || error == null) return null;
  if (error instanceof ApiError && error.status === 403) {
    return <Notice tone="warn">{summaryErrorMessage(error)}</Notice>;
  }
  return (
    <Notice tone="error">
      {summaryErrorMessage(error)}{" "}
      <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={reload}>
        Try again
      </Button>
    </Notice>
  );
}

function DeleteUserReview({
  userId,
  summary,
  flow,
  showIdentity,
  narrow,
}: {
  userId: string;
  summary: UserDeletionSummary;
  flow: DeleteUserFlow;
  showIdentity?: boolean;
  narrow?: boolean;
}) {
  const name = displayName(summary);
  const suggested = summary.suggestedReassignTo;
  const fieldWidth = narrow ? "max-w-md" : undefined;

  return (
    <div className="space-y-5">
      {showIdentity && (
        <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <CommentAvatar name={summary.name || summary.email} className="h-9 w-9" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-slate-900">{summary.name}</div>
            {summary.email && <div className="truncate text-sm text-slate-500">{summary.email}</div>}
          </div>
          <StatusPill value={summary.role} map={USER_TIER_TONES} labels={USER_ROLE_TIER_LABELS} />
        </div>
      )}

      <section>
        <h4 className="mb-2 text-sm font-medium text-slate-900">What they own</h4>
        <OwnershipOverview summary={summary} />
      </section>

      {flow.stale && (
        <Notice tone="warn">
          Something changed after this review loaded, so the delete was stopped. The review below is up to date —
          check it before trying again.
        </Notice>
      )}

      {!summary.canDelete ? (
        <Notice tone="error">{summary.blockedReason || "This user can't be deleted."}</Notice>
      ) : summary.requiresReassignment ? (
        <Notice tone="warn">
          {name} owns {plural(summary.totalToTransfer, "record")}. Pick who takes them over before the account is
          deleted.
        </Notice>
      ) : (
        <Notice tone="success">
          {name} doesn't own any merchants, tickets or other records, so nothing needs to be transferred. You can
          delete the account directly.
        </Notice>
      )}

      {summary.transferItems.length > 0 && <TransferBreakdown items={summary.transferItems} />}

      {summary.canDelete && summary.requiresReassignment && (
        <section>
          <label htmlFor={`reassign-${userId}`} className="mb-1.5 block text-sm font-medium text-slate-900">
            Transfer data to <span className="text-destructive">*</span>
          </label>
          <div className={fieldWidth}>
            <UserSelect
              id={`reassign-${userId}`}
              value={flow.reassignTo || NONE}
              onValueChange={(v) => flow.setReassignTo(v === NONE ? "" : v)}
              pinnedOptions={SELECT_USER_OPTIONS}
              exclude={[userId]}
              className={cn(flow.targetError && "border-destructive")}
            />
          </div>
          {flow.targetError ? (
            <p className="mt-1.5 text-sm text-destructive">{flow.targetError}</p>
          ) : (
            suggested && (
              <p className="mt-1.5 text-xs text-slate-500">
                Suggested: {suggested.name}{suggested.reason === "manager" ? ", their manager" : ""}.
              </p>
            )
          )}
          <p className="mt-1.5 text-xs text-slate-500">
            Everything moves to one user. Comments, tickets and files this user created will show the new owner as
            the author.
          </p>
        </section>
      )}

      {summary.removedItems.length > 0 && (
        <section>
          <h4 className="mb-2 text-sm font-medium text-slate-900">Revoked, not transferred</h4>
          <CountList items={summary.removedItems} className={fieldWidth} />
        </section>
      )}
    </div>
  );
}

function OwnershipOverview({ summary }: { summary: UserDeletionSummary }) {
  const { merchants: m, tickets: t, totalToTransfer: total } = summary;
  const other = otherRecordCount(summary);
  const totalHint = total === 0
    ? "Nothing changes owner"
    : other > 0
      ? `Incl. ${plural(other, "other record")}`
      : "All data that changes owner";

  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Kpi label="Merchants" value={m.total} hint={m.total > 0 ? "Assigned to this user" : "None assigned"} />
      <Kpi label="Open tickets" value={t.assignedOpen} hint="Not yet resolved" />
      <Kpi label="Closed tickets" value={t.assignedClosed} hint="Resolved or closed" />
      <Kpi label="Records to transfer" value={total} hint={totalHint} highlight={total > 0} />
    </dl>
  );
}

function Kpi({ label, value, hint, highlight }: { label: string; value: number; hint: string; highlight?: boolean }) {
  return (
    <div className={cn("rounded-lg border px-4 py-3", highlight ? "border-amber-200 bg-amber-50" : "border-slate-200")}>
      <dt className="text-sm text-slate-600">{label}</dt>
      <dd className={cn("mt-1 text-2xl font-semibold tabular-nums", value === 0 ? "text-slate-400" : "text-slate-900")}>
        {value.toLocaleString()}
      </dd>
      <dd className="mt-0.5 text-xs text-slate-500">{hint}</dd>
    </div>
  );
}

function TransferBreakdown({ items }: { items: UserDeletionItem[] }) {
  const [open, setOpen] = useState(false);
  const known = new Set<string>(GROUP_ORDER);
  const groups = [
    ...GROUP_ORDER.map((g) => ({ key: g, title: GROUP_LABELS[g], items: items.filter((i) => i.group === g) })),
    { key: "other", title: "Other", items: items.filter((i) => !known.has(i.group)) },
  ].filter((g) => g.items.length > 0);

  return (
    <section>
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto p-0"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {open ? "Hide what will be transferred" : "Show what will be transferred"}
      </Button>
      {open && (
        <div className="mt-3 grid gap-x-6 gap-y-4 sm:grid-cols-2">
          {groups.map((g) => (
            <div key={g.key}>
              <h5 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">{g.title}</h5>
              <CountList items={g.items} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CountList({ items, className }: { items: UserDeletionItem[]; className?: string }) {
  return (
    <ul className={cn("divide-y divide-slate-200 rounded-md border border-slate-200 text-sm", className)}>
      {items.map((i) => (
        <li key={i.key} className="flex items-baseline justify-between gap-4 px-4 py-2.5">
          <span className="text-slate-700">{i.label}</span>
          <span className="whitespace-nowrap tabular-nums text-slate-900">
            {i.count.toLocaleString()}
            {i.activeCount != null && (
              <span className="text-slate-500"> · {i.activeCount.toLocaleString()} {ACTIVE_LABELS[i.key] ?? "active"}</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ConsequenceList({ summary, targetName }: { summary: UserDeletionSummary; targetName: string | null }) {
  return (
    <dl className="divide-y divide-slate-200 rounded-md border border-slate-200 text-sm">
      <div className="flex items-baseline justify-between gap-4 px-4 py-2.5">
        <dt className="text-slate-600">Records transferred</dt>
        <dd className="text-right font-medium text-slate-900">
          {targetName ? `${plural(summary.totalToTransfer, "record")} → ${targetName}` : "None"}
        </dd>
      </div>
      {summary.removedItems.map((i) => (
        <div key={i.key} className="flex items-baseline justify-between gap-4 px-4 py-2.5">
          <dt className="text-slate-600">{i.label}</dt>
          <dd className="tabular-nums font-medium text-slate-900">{i.count.toLocaleString()}</dd>
        </div>
      ))}
    </dl>
  );
}
